// lodash
import get from 'lodash/get';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';

import appConstants from '../../constants/appConstants';
import errorCodeConstants from '../../constants/errorCodeConstants';
import {
  URL_CONFIGURATION,
  URL_FEATURES,
  URL_WEB_HANDOVER,
} from '../../constants/endpointConstants';
import { WEB } from '../../constants/applicationTypeConstants';
import { REFRESHING_APPS_ACCESS_TOKEN } from '../../constants/actionConstants';
import {
  reportErrorToNewRelic,
  setCorrelationIdToNewRelic,
  createNewRelicTrackingObject,
  saveNewRelicTrackingEvent,
  endNewRelicTrackingEvent,
} from '../logging/logging-utils';
import getCorrelationId from '../helpers/correlationId';
import fetchWithTimeout from '../helpers/fetchWithTimeout';

function removeTrailingSlash(url = '') {
  return url.replace(/\/$/g, '');
}

function getSessionExpiryData(headers) {
  if (!headers || !headers.get('session-health')) {
    return null;
  }
  return {
    sessionExpiresIn: headers.get('session-health'),
    timestamp: Date.parse(headers.get('date')),
  };
}

function getPropsFromHeaders(sessionId, sessionExpiryData, correlationId, applicationType) {
  if (
    !sessionId &&
    !sessionExpiryData &&
    !applicationType
  ) {
    return undefined;
  }
  return {
    ...(sessionExpiryData ? { sessionExpiryData } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(correlationId ? { correlationId } : {}),
    ...(applicationType ? { applicationType } : {}),
  };
}

async function api(params) {
  const {
    path,
    config,
    hostUrl = '',
    timeout = appConstants.BFF_TIMEOUT,
    getState = () => {},
    dispatch = () => {},
  } = params;

  const applicationType = get(getState(), 'app.applicationType');
  const appsAccessToken = get(getState(), 'mobileApps.auth0AccessToken');

  const { method, body, headers } = config;
  const correlationId = getCorrelationId();

  const ignorePaths = [URL_FEATURES, URL_CONFIGURATION, URL_WEB_HANDOVER];
  let authorizationHeader = {};

  if (!includes(ignorePaths, path)) {
    if (applicationType !== WEB && appsAccessToken) {
      authorizationHeader = {
        Authorization: `Bearer ${appsAccessToken}`,
      };
    } else {
      try {
        const browserAccessToken = await window.Auth0Client.getTokenSilently({
          timeoutInSeconds: appConstants.AUTH0_TOKEN_TIMEOUT,
        });
        if (isString(browserAccessToken)) {
          authorizationHeader = {
            Authorization: `Bearer ${browserAccessToken}`,
          };
        }
      } catch (err) {
        // fail silently so as not to interrupt API call
      }
    }
  }

  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'JL-UI-CORRELATION-ID-V1': correlationId,
      ...authorizationHeader,
      ...headers,
    },
    credentials: 'include',
    timeout,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const host = removeTrailingSlash(hostUrl || window.env.API_HOST);

  const sessionNewRelicTrackingObject = createNewRelicTrackingObject();
  saveNewRelicTrackingEvent(sessionNewRelicTrackingObject);

  return fetchWithTimeout(`${host}/${path}`, options) // eslint-disable-line no-undef
    .then((response) => {
      const sessionId = response.headers.get('JL-SESSION-ID');
      const sessionExpiryData = getSessionExpiryData(response.headers);
      const applicationTypeHeader = response.headers.get('Application-Type');

      if (
        applicationType !== WEB &&
        !includes(ignorePaths, path) &&
        get(getState(), 'delivery.getDeliveryPageApiCallComplete') &&
        !get(getState(), 'mobileApps.refreshingAppsAccessToken')
      ) {
        // refresh the mobile token
        const requestAccessToken = get(getState(), 'mobileApps.requestAccessToken');
        if (isFunction(requestAccessToken)) {
          dispatch({
            type: REFRESHING_APPS_ACCESS_TOKEN,
          });
          requestAccessToken();
        }
      }

      setCorrelationIdToNewRelic(correlationId);

      endNewRelicTrackingEvent(sessionNewRelicTrackingObject);

      const propsFromHeaders = getPropsFromHeaders(sessionId, sessionExpiryData, correlationId, applicationTypeHeader);

      if (response.status === 204 || response.status === 205) {
        const json = propsFromHeaders ? { propsFromHeaders } : undefined;
        return Promise.resolve(json);
      }

      return response.json().then((json) => {
        if (!response.ok) {
          return Promise.reject({
            json: {
              ...json,
              status: response.status,
              ...(propsFromHeaders ? { propsFromHeaders } : {}),
            },
          });
        }

        return Promise.resolve({
          ...json,
          ...(propsFromHeaders ? { propsFromHeaders } : {}),
        });

      }).catch((error = {}) => {

        if (includes([422, 400, 403, 404, 500, 502, 503, 504], response.status)) {
          reportErrorToNewRelic(error, {
            path,
            code: get(error, 'json.code'),
            message: get(error, 'json.message'),
            status: get(error, 'json.status'),
            correlationId,
            sessionId,
          });
        }

        if (response.ok) {
          return Promise.resolve(propsFromHeaders);
        }

        // JSON parsing failure
        if (!error.json) {
          return Promise.reject({
            code: errorCodeConstants.CLIENT_UNKNOWN_TECHNICAL_ERROR, status: response.status,
          });
        }

        // error with valid JSON
        return Promise.reject({
          ...error.json,
          ...(propsFromHeaders ? { propsFromHeaders } : {}),
        });

      });

    }).catch((error = {}) => {

      if (error.name === 'AbortError') {
        reportErrorToNewRelic(error, {
          path,
          code: errorCodeConstants.CLIENT_API_TIMEOUT,
          message: error.message,
          correlationId,
        });

        return Promise.reject({
          code: errorCodeConstants.CLIENT_API_TIMEOUT,
        });
      }

      if (!error.status) {
        return Promise.reject({
          code: errorCodeConstants.CLIENT_CONNECTIVITY_ERROR,
        });
      }

      // rejects from response.json().catch()
      return Promise.reject(error);

    });
}

export default api;
